import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CalendarRange, Clapperboard, LayoutDashboard, LogOut, Menu, MessageCircleHeart, MousePointerClick,
  PanelLeftClose, PanelLeftOpen, Settings, Smartphone, Users, Wallet,
} from 'lucide-react'
import { configure, supabase } from './supabase.ts'
import { useSession } from './auth.tsx'
import { usePeriode } from './periode.tsx'
import { fileAtraiter } from './requetes.ts'

interface Onglet { chemin: string; libelle: string; compteur?: boolean }
interface Entree { chemin: string; libelle: string; icone: ReactNode; onglets?: Onglet[]; autres?: string[]; compteur?: boolean }

// Menu court : une entree par espace, ses ecrans sont des onglets en haut de page.
export const NAVIGATION: Entree[] = [
  { chemin: '/tableau-de-bord', libelle: 'Tableau de bord', icone: <LayoutDashboard size={18} /> },
  { chemin: '/revenue', libelle: 'Revenus', icone: <Wallet size={18} /> },
  { chemin: '/acquisition', libelle: 'Acquisition', icone: <MousePointerClick size={18} />, onglets: [
    { chemin: '/acquisition', libelle: 'Vue d’ensemble' },
    { chemin: '/acquisition/liens', libelle: 'Liens' },
    { chemin: '/acquisition/direct', libelle: 'Derniers subs' },
    { chemin: '/acquisition/comptes', libelle: 'Comptes et posts' },
  ] },
  { chemin: '/farm/fleet', libelle: 'Phone Farm', icone: <Smartphone size={18} />, onglets: [
    { chemin: '/farm/fleet', libelle: 'Fleet' },
    { chemin: '/farm/planning', libelle: 'Planning' },
    { chemin: '/farm/comptes', libelle: 'Comptes' },
    { chemin: '/farm/sante', libelle: 'Santé' },
    { chemin: '/farm/analytics', libelle: 'Analytics' },
    { chemin: '/farm/model', libelle: 'Model' },
  ] },
  { chemin: '/content/fabrique', libelle: 'Content', icone: <Clapperboard size={18} />, onglets: [
    { chemin: '/content/fabrique', libelle: 'Fabrique' },
    { chemin: '/captions', libelle: 'Captions' },
  ] },
  { chemin: '/equipe', libelle: 'Équipe', icone: <Users size={18} />, autres: ['/geelark'], onglets: [
    { chemin: '/equipe', libelle: 'Équipe et paie' },
    { chemin: '/analytics-va', libelle: 'Analytics VA' },
    { chemin: '/liens', libelle: 'Liens GetMySocial' },
    { chemin: '/manageuses', libelle: 'Manageuses' },
    { chemin: '/geelark', libelle: 'Espace VA GeeLark' },
  ] },
  { chemin: '/chatting', libelle: 'Chatting', icone: <MessageCircleHeart size={18} /> },
  { chemin: '/a-traiter', libelle: 'Admin', icone: <Settings size={18} />, compteur: true, onglets: [
    { chemin: '/a-traiter', libelle: 'À traiter', compteur: true },
    { chemin: '/automatisations', libelle: 'Automatisations' },
    { chemin: '/systeme', libelle: 'Santé' },
    { chemin: '/design', libelle: 'Planche de style' },
  ] },
]

const sousChemin = (pathname: string, chemin: string) => pathname === chemin || pathname.startsWith(chemin + '/')
const entreeDe = (pathname: string) => NAVIGATION.find((e) =>
  [e.chemin, ...(e.onglets ?? []).map((o) => o.chemin), ...(e.autres ?? [])].some((c) => sousChemin(pathname, c)))
/** Un onglet racine (/acquisition) n est actif que seul : ses voisins (/acquisition/liens) ont leur propre onglet. */
function ongletActif(pathname: string, onglets: Onglet[], o: Onglet) {
  const plusPrecis = onglets.some((x) => x !== o && x.chemin.startsWith(o.chemin + '/') && sousChemin(pathname, x.chemin))
  return sousChemin(pathname, o.chemin) && !plusPrecis
}

const CLE_REPLIE = 'muse.menu.replie'
function lireReplie() { try { return localStorage.getItem(CLE_REPLIE) === '1' } catch { return false } }
function ecrireReplie(v: boolean) { try { localStorage.setItem(CLE_REPLIE, v ? '1' : '0') } catch { /* simple confort */ } }

export function Coque({ children }: { children: ReactNode }) {
  const { session } = useSession()
  const { periode } = usePeriode()
  const { pathname } = useLocation()
  const [ouvert, setOuvert] = useState(false)
  const [replie, setReplie] = useState(lireReplie)
  const entree = entreeDe(pathname)
  const file = useQuery({ queryKey: ['file'], queryFn: fileAtraiter, enabled: configure && !!session })
  const aTraiter = file.data?.length ?? 0

  useEffect(() => { setOuvert(false) }, [pathname])
  // Le nombre de sujets a trancher vit aussi dans le titre de l onglet (D1).
  useEffect(() => { document.title = aTraiter > 0 ? `(${aTraiter}) MUSE` : 'MUSE' }, [aTraiter])

  return (
    <div className="ds">
      <div className="ds-coque" data-replie={replie}>
        <div className="ds-voile" data-ouvert={ouvert} onClick={() => setOuvert(false)} />
        <nav className="ds-nav" data-ouvert={ouvert} aria-label="Navigation">
          <div className="ds-marque">
            <span className="ds-texte-degrade ds-marque__nom">MUSE</span>
            <button className="ds-icone ds-nav__replier" aria-label={replie ? 'Déplier le menu' : 'Replier le menu'} title={replie ? 'Déplier le menu' : 'Replier le menu'}
              onClick={() => { setReplie(!replie); ecrireReplie(!replie) }}>
              {replie ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>
          <div className="ds-nav__groupe">
            {NAVIGATION.map((e) => (
              <Link key={e.chemin} to={e.chemin} title={replie ? e.libelle : undefined} aria-label={e.libelle}
                className={`ds-nav__lien${entree === e ? ' active' : ''}`} aria-current={entree === e ? 'page' : undefined}>
                {e.icone}<span className="ds-nav__libelle">{e.libelle}</span>
                {e.compteur && aTraiter > 0 && <span className="ds-nav__compteur">{aTraiter}</span>}
              </Link>
            ))}
          </div>
        </nav>
        <div className="ds-principal">
          <header className="ds-barre">
            <button className="ds-icone ds-menu-mobile" aria-label="Menu" onClick={() => setOuvert(true)}><Menu size={18} /></button>
            <span className="ds-barre__periode"><CalendarRange size={16} /> {periode.libelle}</span>
            {!configure && <span className="ds-badge ds-badge--alerte">Base non configurée</span>}
            <div className="ds-barre__droite">
              {session && <span className="ds-discret" style={{ fontWeight: 600 }}>{session.user.email}</span>}
              {session && <button className="ds-icone" aria-label="Déconnexion" title="Déconnexion" onClick={() => supabase?.auth.signOut()}><LogOut size={16} /></button>}
            </div>
          </header>
          <main className="ds-contenu">
            {entree?.onglets && (
              <div className="ds-onglets" role="navigation" aria-label={entree.libelle}>
                {entree.onglets.map((o) => (
                  <Link key={o.chemin} to={o.chemin} className={`ds-onglet${ongletActif(pathname, entree.onglets!, o) ? ' active' : ''}`}>
                    {o.libelle}{o.compteur && aTraiter > 0 && <span className="ds-nav__compteur">{aTraiter}</span>}
                  </Link>
                ))}
              </div>
            )}
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
